import { useNavigate } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';

import { previewPracticeNames } from 'api/Practice';

const usePracticeNameCheck = (field, savedValue) => {
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const lastChecked = useRef(null);
  const requestId = useRef(0);
  const inFlight = useRef(null);
  const holdsSave = useRef(false);

  const run = useCallback(
    async (value, bySaveGesture) => {
      const id = ++requestId.current;
      const response = await previewPracticeNames(navigate, { [field]: value }).catch(() => null);
      if (id !== requestId.current) return false;

      if (response?.code !== 0) {
        lastChecked.current = null;
        setSuggestion(null);
        return false;
      }

      const saved = response?.[field];
      const collapsed = value.trim().replace(/\s+/g, ' ');
      const nextSuggestion = saved && saved !== collapsed ? saved : null;
      setSuggestion(nextSuggestion);
      holdsSave.current = bySaveGesture && !!nextSuggestion;
      return !!nextSuggestion;
    },
    [field, navigate]
  );

  /**
   * Previews what the rules will store for `typed`. A check the save gesture itself started
   * (the blur a save button causes, or a save finding the name unchecked) holds the next save
   * once it shows a new suggestion, so the person sees the preview before anything is stored.
   * A check started by leaving the field another way only shows the preview.
   */
  const check = useCallback(
    (typed, { bySaveGesture = false } = {}) => {
      const value = typed ?? '';
      if (!value.trim() || value === lastChecked.current) return Promise.resolve(false);
      lastChecked.current = value;
      if (value === savedValue) {
        setSuggestion(null);
        return Promise.resolve(false);
      }
      inFlight.current = run(value, bySaveGesture);
      return inFlight.current;
    },
    [run, savedValue]
  );

  /**
   * Asked by the form on save. Resolves true once, when a check the save gesture started
   * produced a suggestion the person has not been able to act on yet.
   */
  const gate = useCallback(
    async (current) => {
      const value = current ?? '';
      if (value.trim() && value !== lastChecked.current) {
        await check(value, { bySaveGesture: true });
      } else {
        await inFlight.current;
      }
      if (!holdsSave.current) return false;
      holdsSave.current = false;
      return true;
    },
    [check]
  );

  const reset = useCallback(() => {
    requestId.current += 1;
    lastChecked.current = null;
    inFlight.current = null;
    holdsSave.current = false;
    setSuggestion(null);
  }, []);

  return { suggestion, check, gate, reset };
};

export default usePracticeNameCheck;
